import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, Upload, X } from 'lucide-react';
import { cn } from '../../lib/cn.js';

/**
 * 1.58 — what stands where the camera stands, on a screen that has none.
 *
 * A desktop has no lens to hold up to a page, so the pages arrive as files:
 * dropped on this, or chosen from the box that a click opens. Under the zone is
 * what the camera's strip is under the viewfinder, made for a screen with room:
 * a file is in the list the moment it is taken up, a spinner while it is read,
 * and then the page it came to. What comes after is the review, unchanged.
 *
 * Not every file is a page. Where the dialog can file a document as it is, a PDF
 * or a Word file dropped here never gets into this list: it is handed on, each
 * as its own document, and only a photograph is read, so the words under the
 * zone say which is which.
 */

/** What a page has to say for its tile. The dialog's own page is one. */
interface Tile {
  id: string;
  /** The rendered preview, or null while it is still being made. */
  thumb: string | null;
  /** A PDF that is passed through as it is: there is no preview to wait for. */
  file: File | null;
}

/** A dragged piece of text or a link is not a file, and does not light the zone up. */
const carriesFiles = (data: DataTransfer) => data.types.includes('Files');

export function DropStage({
  pages,
  reading,
  filesAsIs,
  onPick,
  onFiles,
  onRemove,
}: {
  pages: Tile[];
  /** Files that have been taken up and are not pages yet: the ones being read now. */
  reading: number;
  /** A document is filed as it is and a photograph is a page, so the zone says so; otherwise every file is a page. */
  filesAsIs: boolean;
  /** The zone was pressed: open the picker. */
  onPick: () => void;
  onFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  /* `dragenter` and `dragleave` fire for every element the pointer crosses, so a
     plain on and off would flicker, or go off while the pointer is still inside.
     Counting the ways in and the ways out keeps it on until the last one is out. */
  const depth = useRef(0);

  return (
    // The whole stage takes the drop, not only the zone: a file let go a little
    // low would otherwise be opened by the browser in place of this page.
    <div
      className="space-y-3"
      onDragEnter={(event) => {
        if (!carriesFiles(event.dataTransfer)) return;
        event.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(event) => {
        if (!carriesFiles(event.dataTransfer)) return;
        // Without this the browser takes the drop for a refusal, and opens the
        // file in the tab instead of handing it to the page.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(event) => {
        event.preventDefault();
        depth.current = 0;
        setOver(false);
        const files = [...event.dataTransfer.files];
        if (files.length > 0) onFiles(files);
      }}
    >
      <button
        type="button"
        onClick={onPick}
        className={cn(
          'w-full min-h-40 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors',
          over ? 'border-primary bg-primary/10' : 'border-border bg-muted/40 hover:bg-muted',
        )}
      >
        <Upload className="h-8 w-8 text-muted-foreground" aria-hidden />
        <span className="text-sm font-medium">{t('scan.dropTitle')}</span>
        <span className="text-xs text-muted-foreground">
          {t(filesAsIs ? 'scan.dropTypes' : 'scan.dropTypesPages')}
        </span>
      </button>

      {(pages.length > 0 || reading > 0) && (
        <ul className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {pages.map((page, index) => (
            <li
              key={page.id}
              className="relative rounded-lg border border-border bg-muted overflow-hidden aspect-[3/4]"
            >
              {page.file ? (
                <span className="w-full h-full flex flex-col items-center justify-center gap-1 text-muted-foreground">
                  <FileText className="h-6 w-6" aria-hidden />
                  <span className="text-[10px] px-1 truncate max-w-full">{page.file.name}</span>
                </span>
              ) : page.thumb ? (
                <img src={page.thumb} alt="" className="w-full h-full object-contain" />
              ) : (
                <span className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                </span>
              )}

              <span className="absolute top-1 start-1 px-1.5 rounded-md bg-background/85 text-[11px] font-semibold tabular-nums">
                {index + 1}
              </span>

              <button
                type="button"
                onClick={() => onRemove(page.id)}
                aria-label={t('scan.removePage', { index: index + 1 })}
                className="absolute top-1 end-1 p-1 rounded-md bg-background/85 text-destructive hover:bg-destructive/10"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}

          {/* The files that are still being read, after the pages that are: they
              are the next pages, in the order they were taken up. */}
          {Array.from({ length: reading }, (_, index) => (
            <li
              key={`reading-${index}`}
              className="rounded-lg border border-dashed border-border bg-muted/40 aspect-[3/4] flex items-center justify-center text-muted-foreground"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">{t(filesAsIs ? 'scan.dropHint' : 'scan.dropHintPages')}</p>
    </div>
  );
}
